"use client";

import { Card, Stack, Group, Box, Text, Title, Divider, Button, ActionIcon, Tooltip, Badge, Image, SimpleGrid } from "@forestsync/ui";
import { TbExternalLink, TbMapPin, TbCalendar, TbTree, TbHourglass } from "react-icons/tb";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { getCountryName } from "@forestsync/db";

export type Sink = {
  id: bigint;
  refId: string;
  name: string;
  description: string | null;
  country: string;
  region: string;
  methodology: string | null;
  creditType: string | null;
  sizeHa: number;
  lifespan: number | null;
  totalTokens: number;
  availableTokens: number;
  askVolume: number;
  bestAsk: number | null;
  lastPrice: number | null;
  lastTradeVolume: number | null;
  lastTradeAt: Date | null;
  owner: { name: string } | null;
  galleryUrls?: string[];
  ownerLogoUrl?: string | null;
};

interface ProjectCardProps {
  sink: Sink;
  hideActions?: boolean;
  marketVintage?: number;
  vintageCount?: number;
  isGuest?: boolean;
}

const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100";

export function ProjectCard({ sink, hideActions, marketVintage, vintageCount, isGuest }: ProjectCardProps) {
  const t = useTranslations("landing");
  const tb = useTranslations("browse");

  const marketHref = marketVintage
    ? `/market/${sink.refId}/${marketVintage}`
    : `/market`

  const isSoldOut = marketVintage !== undefined && sink.bestAsk === null;

  const getImageByCreditType = (type: string | null, refId: string) => {
    const tStr = type?.toLowerCase() || "";

    let hash = 0;
    for (let i = 0; i < refId.length; i++) hash += refId.charCodeAt(i);
    const idx = hash % 3;

    if (tStr.includes("biochar")) {
      const imgs = ["/images/biochar.png", "/images/biochar1.png", "/images/biochar3.png"];
      return imgs[idx];
    }
    if (tStr.includes("afforestation")) {
      const imgs = ["/images/aff.png", "/images/aff2.png", "/images/aff3.png"];
      return imgs[idx];
    }
    if (tStr.includes("reforestation") || tStr.includes("forest")) {
      const imgs = ["/images/reforest.png", "/images/reforest1.png", "/images/reforest2.png"];
      return imgs[idx];
    }
    if (tStr.includes("redd")) {
      const imgs = ["/images/redd.png", "/images/redd0.png", "/images/redd1.png"];
      return imgs[idx];
    }
    if (tStr.includes("soil")) {
      const imgs = ["/images/soil.png", "/images/soil2.png", "/images/soil3.png"];
      return imgs[idx];
    }
    if (tStr.includes("blue") || tStr.includes("mangrove")) {
      const imgs = ["/images/blue1.png", "/images/blue2.png", "/images/blue3.png"];
      return imgs[idx];
    }
    if (tStr.includes("renew") || tStr.includes("energy")) {
      return "/images/renew0.png";
    }
    return "/images/reforest.png";
  };

  const coverImage = sink.galleryUrls?.[0] ?? getImageByCreditType(sink.creditType ?? sink.methodology, sink.refId);

  return (
    <Card
      key={String(sink.id)}
      withBorder
      shadow="sm"
      radius="md"
      padding="md"
      h="100%"
      className="flex flex-col"
    >
      <Card.Section>
        <Link href={marketHref} style={{ display: "block", position: "relative" }}>
          <Image
            src={coverImage}
            alt={sink.name}
            h={200}
            fit="cover"
            fallbackSrc="https://placehold.co/600x400?text=Project+Image"
          />
          {sink.ownerLogoUrl && (
            <div className="absolute top-2 right-2 bg-white/90 dark:bg-gray-900/90 rounded border border-white/20 dark:border-gray-700 px-1.5 py-[3px] pointer-events-none leading-none">
              <img
                src={sink.ownerLogoUrl}
                alt={sink.owner?.name ?? ""}
                className="h-9 w-auto max-w-[84px] object-contain block"
              />
            </div>
          )}
        </Link>
      </Card.Section>

      <Box mt="md" className="flex-1 flex flex-col">
        <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs" mih={48}>
          <Text fw={700} size="lg" lineClamp={2} lh={1.2}>
            {sink.name}
          </Text>
          <Badge color="blue" variant="light" size="sm" className="shrink-0">
            {getCountryName(sink.country)}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed" lineClamp={2} mb="md" mih={42}>
          {sink.description
            ? sink.description
            : `${sink.owner?.name ? `Developed by ${sink.owner.name}. ` : ""}Verified project located in ${sink.region}, generating high-quality ${sink.creditType?.toLowerCase()} credits.`
          }
        </Text>

        <Box mt="auto">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="xs">
            {t("projectDetails")}
          </Text>

          <Group gap="xs" mb="md">
            {(sink.creditType ?? sink.methodology) && (
              <Badge size="sm" variant="outline" color="green" leftSection={<TbTree size={12} />}>
                {sink.creditType ?? sink.methodology}
              </Badge>
            )}
            {sink.lifespan ? (
              <Badge size="sm" variant="outline" color="teal" leftSection={<TbHourglass size={12} />}>
                {sink.lifespan}+
              </Badge>
            ) : null}
            {vintageCount && vintageCount > 0 ? (
              <Badge size="sm" variant="outline" color="orange" leftSection={<TbCalendar size={12} />}>
                {t("vintageCount", { count: vintageCount })}
              </Badge>
            ) : null}
          </Group>
        </Box>
      </Box>

      <Card.Section style={{ borderTop: "1px solid var(--mantine-color-default-border)" }} mx="md" pt="md" pb="xs">
        <SimpleGrid cols={2} spacing="md">
          <Box>
            <Text size="xs" c="dimmed" fw={500} tt="uppercase">{t("available")}</Text>
            <Text fw={700} size="md" c={isSoldOut ? "orange" : undefined}>
              {sink.askVolume > 0
                ? `${Math.round(sink.askVolume).toLocaleString()} tCO₂e`
                : marketVintage !== undefined
                  ? tb("soldOut")
                  : "N/A"}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed" fw={500} tt="uppercase">
              {sink.bestAsk !== null
                ? t("priceFrom")
                : sink.lastPrice !== null
                  ? t("lastPrice")
                  : t("price")}
            </Text>
            <Text fw={700} size="md" c={sink.bestAsk !== null ? "green" : "dimmed"}>
              {sink.bestAsk !== null
                ? `€${sink.bestAsk.toFixed(2)}`
                : sink.lastPrice !== null
                  ? `€${sink.lastPrice.toFixed(2)}`
                  : "N/A"}
            </Text>
          </Box>
        </SimpleGrid>
      </Card.Section>

      {!hideActions && (
        <Group mt="md" gap="sm" wrap="nowrap">
          {isGuest ? (
            <Button
              component={Link}
              href={marketHref}
              size="md"
              variant="light"
              color="blue"
              className="flex-1"
            >
              View Details
            </Button>
          ) : isSoldOut ? (
            <Tooltip label={tb("soldOutDesc")} withArrow>
              <Button size="md" variant="filled" color="orange" disabled style={{ flex: 1 }}>
                {tb("soldOut")}
              </Button>
            </Tooltip>
          ) : marketVintage ? (
            <Button
              component={Link}
              href={marketHref}
              size="md"
              variant="filled"
              color="blue"
              className="flex-1"
            >
              Buy Credits
            </Button>
          ) : (
            <Tooltip label={t("noActiveVintage")} withArrow>
              <Button size="md" variant="filled" disabled style={{ flex: 1 }}>
                Buy Credits
              </Button>
            </Tooltip>
          )}
          <Tooltip label={t("viewInRegistry")} withArrow>
            <ActionIcon
              component="a"
              href={`${REGISTRY_URL}/sink/${sink.refId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="default"
              size="42px"
            >
              <TbExternalLink size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>
      )}
    </Card>
  );
}
